import { DislikeOutlined, LikeOutlined } from "@ant-design/icons";
import { Alert, Button, Tag, Typography } from "antd";
import type React from "react";
import type { AssistantAnswer, Vote } from "../assistant-types";

const GENERATED_LABEL =
  "Generated from Eden/Echo code and past tickets, not reviewed. Check before acting on money matters.";

interface AnswerViewProps {
  answer: AssistantAnswer;
  elapsedMs?: number;
  vote?: Vote;
  onRate: (vote: Vote) => void;
}

export const AnswerView: React.FC<AnswerViewProps> = ({ answer, elapsedMs, vote, onRate }) => (
  <div className="flex flex-col gap-2">
    <Typography.Text strong>{answer.headline}</Typography.Text>
    {answer.clarifyingQuestion && <Typography.Text>❓ {answer.clarifyingQuestion}</Typography.Text>}
    {answer.steps.length > 0 && (
      <ol className="m-0 list-decimal pl-5">
        {answer.steps.map((step) => (
          <li key={step}>{step}</li>
        ))}
      </ol>
    )}
    {answer.whoCanDoIt.length > 0 && (
      <Typography.Text>
        <strong>Who can do it:</strong> {answer.whoCanDoIt.join(", ")}
      </Typography.Text>
    )}
    {answer.disagreement && (
      <Alert type="warning" showIcon message="Sources disagree" description={answer.disagreement} />
    )}
    {answer.couldNotCheck.length > 0 && (
      <Typography.Text type="secondary">
        Couldn&apos;t check: {answer.couldNotCheck.join(", ")}
      </Typography.Text>
    )}
    {answer.imageDescription && (
      <Typography.Text type="secondary" italic>
        Screenshot: {answer.imageDescription}
      </Typography.Text>
    )}
    {answer.sources.length > 0 && (
      <div className="flex flex-wrap gap-1">
        {answer.sources.map((source) => (
          <Tag key={`${source.kind}:${source.title}`}>{source.title}</Tag>
        ))}
      </div>
    )}
    <Typography.Text type="secondary" className="text-xs">
      {GENERATED_LABEL}
      {elapsedMs !== undefined && ` · ${(elapsedMs / 1000).toFixed(1)} s`}
      {answer.system !== "unknown" && ` · ${answer.system}`}
    </Typography.Text>
    <div className="flex gap-2">
      <Button
        size="small"
        icon={<LikeOutlined />}
        type={vote === "up" ? "primary" : "default"}
        onClick={() => onRate("up")}
      >
        Helpful
      </Button>
      <Button
        size="small"
        icon={<DislikeOutlined />}
        type={vote === "down" ? "primary" : "default"}
        onClick={() => onRate("down")}
      >
        Not helpful
      </Button>
    </div>
  </div>
);
